import { Rfx } from "newui/features/Rfx/types/rfx.types";
import {
	QuotesDisplayModel,
	ReturnableScheduleDisplayModel,
	RfxSubmission,
	SubmissionDisplay,
	SubmissionPayload,
} from "./types/submission.type";
import { QUESTION_TYPE_ID, RFX_QUESTION_TYPE } from "./constants";
import {
	CorporateDocumentResponses,
	CorporateDocumentsAnswer,
	RfxAnswer,
	RfxAnswerChoiceOfType,
	RfxAnswerResponse,
	RfxAnswers,
	RfxAnswerType,
} from "./types/returnable-schedule-answer.type";
import { RfxQuestion } from "../types/returnable-schedule-question.type";
import { RfxQuoteAnswers } from "./types/quote.type";
import { TextLength } from "../types/component-types";

export const initialRfxRequestData = {
	hasKeyPersonnel: false,
	hasPastProjects: false,
	rfx: null,
	rfxInviteId: null,
	quotes: {
		answers: [],
		cells: [],
		name: "quotes",
		type: null,
		templateFile: null,
		responseFiles: [],
		questions: [],
		flexibleResponseFiles: [],
	} as QuotesDisplayModel,
	returnableSchedules: {
		answers: [],
		name: "returnableSchedules",
		type: null,
		templateFile: null,
		responseFiles: [],
		questions: [],
	} as ReturnableScheduleDisplayModel,
} as SubmissionDisplay;

export const getSubmissionDataModel = (
	rfx: Rfx,
	rfxSubmission: RfxSubmission,
	hasKeyPersonnel: boolean,
	hasPastProjects: boolean
): SubmissionDisplay => {
	const submission = rfxSubmission?.submission;

	// ensure sections in correct order
	rfx?.questionnaire?.sort((a, b) => a.sort_order - b.sort_order);

	// ensure questions in correct order
	rfx?.questionnaire?.forEach((section) => {
		section.questionnaire.sort((a, b) => a.sort_order - b.sort_order);
	});

	// Quote row/cell data (PRO-688 follow-up): `rfx` here may be the ambient
	// RfxDataProvider RFx — fetched invite-agnostic (`GET rfq/{id}`, backed
	// by RFQ::scopeWithQuote), whose `cells` are hardcoded to
	// `whereNull('rfx_invite_id')` and therefore never carry a supplier's
	// own cells (descriptions on supplier-created rows, most visibly, but
	// any supplier-owned cell would be dropped the same way if it weren't
	// also duplicated into `submission.rfxQuoteCells` for Formik).
	// `rfxSubmission.rfq` is the invite-scoped RFx from this same request
	// (RfxInviteController::getSubmission -> getInviteWithSubmission ->
	// scopeWithQuestionAndQuoteData, which threads the invite id and
	// includes procurer-fixed AND this invite's own cells) — prefer it for
	// the quote tables, falling back to `rfx.quotes` only when no
	// invite-scoped RFx is available (e.g. creation/template flows that
	// call this helper with no live submission).
	const quoteSource = rfxSubmission?.rfq?.quotes ?? rfx?.quotes;

	// ensure quote questions in correct order
	quoteSource?.sort((a, b) => a.sortOrder - b.sortOrder);

	// ensure quote lines in correct order
	quoteSource?.forEach((quote) => {
		quote.rows?.sort((a, b) => a.sortOrder - b.sortOrder);
	});

	// Merge invite-level revision info into global rfx quotes
	const quoteRevisionMap = rfxSubmission?.quoteRevisionInfo;
	const quotesWithRevisions = quoteRevisionMap
		? quoteSource?.map((quote) => {
				const revInfo = quoteRevisionMap[quote.id];
				if (revInfo) {
					return {
						...quote,
						current_revision: revInfo.current_revision,
						has_revisions: revInfo.has_revisions,
					};
				}
				return quote;
			})
		: quoteSource;

	const updatedRfxResponseData = {
		hasKeyPersonnel: hasKeyPersonnel,
		hasPastProjects: hasPastProjects,
		rfx: rfx,
		rfxInviteId: rfxSubmission?.id,
		quotes: {
			answers: submission?.rfxQuoteAnswers,
			cells: submission?.rfxQuoteCells ?? [],
			name: "quotes",
			type: rfx?.quoteType,
			templateFile: rfx?.quoteFile,
			responseFiles: submission?.quote_files,
			questions: quotesWithRevisions,
			flexibleResponseFiles: submission?.flexible_quote_files,
		} as QuotesDisplayModel,
		returnableSchedules: {
			answers: submission?.rfxAnswers,
			name: "returnableSchedules",
			type: rfx?.returnableScheduleType,
			templateFile: rfx?.returnableScheduleFile,
			responseFiles: submission?.returnable_schedule_file_responses,
			questions: rfx?.questionnaire,
		} as ReturnableScheduleDisplayModel,
	} as SubmissionDisplay;

	return updatedRfxResponseData;
};

export function isCorporateDocOfChosenType<T extends RfxAnswerChoiceOfType>(
	value: CorporateDocumentsAnswer,
	chosenType: T
): value is Extract<CorporateDocumentResponses, { type: T }> {
	return value.chosen_type === chosenType;
}

export function isAnswerOfType<T extends RfxAnswerType>(
	value: RfxAnswerResponse,
	type: T
): value is Extract<RfxAnswerResponse, { type: T }> {
	return value.type === type;
}

/**
 * Checks if the provided answer type is a smart field or not
 * @param type
 * @returns boolean
 */
export const isSmartField = (type: RfxAnswerType): boolean => {
	return (
		type === RFX_QUESTION_TYPE.PROJECT_EXPERIENCE ||
		type === RFX_QUESTION_TYPE.KEY_PERSONNEL ||
		type === RFX_QUESTION_TYPE.CORPORATE_DOCUMENTS
	);
};

/* 
    This function maps the original load of questions and answers from the API
    to the current answers provided by the user.
*/
export const mapAnswersToQuestions = (
	questionsAndAnswers: SubmissionPayload,
	submissionPayload: any
) => {
	const result = structuredClone(questionsAndAnswers);
	const returnableSchedule = result[0];
	const returnableScheduleAnswers = returnableSchedule?.answers as RfxAnswers;
	const quotes = result[1];
	const quoteAnswers = quotes?.answers as RfxQuoteAnswers;

	if (quoteAnswers) {
		for (const question of quotes.questions) {
			for (const item of question.items) {
				const answerRow = quoteAnswers.find(
					(answer) => answer.rfqQuoteItemId === item.id
				);

				const updatedValues = submissionPayload.quote_answer[item.id];
				answerRow.answer.value = updatedValues;
			}
		}
	}

	for (const section of returnableSchedule.questions) {
		for (const question of section.questionnaire) {
			const answerData = returnableScheduleAnswers.find(
				(answer) => answer.rfqQuestionId === question.id
			);
			const updatedValues = submissionPayload.question_answer[question.id];
			answerData.answer.value = updatedValues;
		}
	}

	return result;
};

export const getAnswerForQuestion = (
	question: RfxQuestion,
	allAnswers: RfxAnswers
): RfxAnswer | undefined => {
	return allAnswers?.find((answer) => answer.rfqQuestionId === question.id);
};

const convertNullToEmptyString = (tableData) => {
	return tableData?.map((row) => {
		const values = row?.values?.map((value) => {
			return value ?? "";
		});

		return { ...row, values: values };
	});
};

export const filterFieldValues = (question: RfxQuestion) => {
	switch (question.type) {
		case QUESTION_TYPE_ID.CORPORATE_DOCUMENTS: {
			return question?.fields;
		}
		case QUESTION_TYPE_ID.TABLE:
		case QUESTION_TYPE_ID.TABLE_ROWS: {
			// @ts-ignore
			return convertNullToEmptyString(question?.fields?.table);
		}
		default: {
			// @ts-ignore
			return question?.fields?.option;
		}
	}
};

export const getTextLengthValue = (length?: TextLength): number => {
	switch (length) {
		case TextLength.SHORT:
			return 1500;
		case TextLength.LONG:
			return 5000;
		case TextLength.L4500:
			return 4500;
		default:
			return 1500; // default value if undefined
	}
};
